import { SelectTreatmentBillsFormField } from "@/components/features/matter/medical-treatments/SelectTreatmentBillsFormField";
import { SelectTreatmentRecordFormField } from "@/components/features/matter/medical-treatments/SelectTreatmentRecordsFormField";
import {
  Form,
  FormSubmitButton,
  SheetFooter,
  Slideover,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useUpdateMedicalTreatmentAppointment,
  useUpdateMedicalTreatmentAppointmentConnections,
} from "@/lib/data/react-query/matters/medical-treatment-appointment";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { MedicalTreatmentAppointment } from "@/lib/types";
import { formatDate, formatTime } from "@/lib/utils/format";
import { MedicalTreatmentAppointmentIcon } from "@/lib/utils/iconUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useSuspenseQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AssociateTreatmentAppointmentsSlideoverProps {
  title?: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  treatmentId: string;
  appointmentIds: string[];
  onSuccess?: () => void;
}

const associateAppointmentsSchema = z.object({
  medicalTreatmentRecordId: z.array(z.string()).optional(),
  medicalTreatmentBillId: z.array(z.string()).optional(),
  recordNotApplicable: z.boolean().optional(),
  billNotApplicable: z.boolean().optional(),
});

const getCommonIds = (collections: string[][]) => {
  if (collections.length === 0) {
    return [];
  }
  return collections
    .slice(1)
    .reduce<
      string[]
    >((acc, current) => acc.filter((id) => current.includes(id)), collections[0]);
};

const getAppointmentRecordIds = (
  appointment: MedicalTreatmentAppointment & {
    records?: { id: string }[];
  },
) => (appointment.records ?? []).map((record) => record.id);

const getAppointmentBillIds = (
  appointment: MedicalTreatmentAppointment & {
    bills?: { id: string }[];
  },
) => (appointment.bills ?? []).map((bill) => bill.id);

export const AssociateTreatmentAppointmentsSlideover = ({
  title = SLIDEOVER_TITLE.TREATMENT_APPOINTMENT_ASSOCIATION.CREATE,
  description = SLIDEOVER_DESCRIPTION.TREATMENT_APPOINTMENT_ASSOCIATION.CREATE,
  open,
  setOpen,
  matterId,
  treatmentId,
  appointmentIds,
  onSuccess,
}: AssociateTreatmentAppointmentsSlideoverProps) => {
  const { hasActionPermission } = useUser();
  const canUpdateAppointments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canManageAssociations =
    canUpdateAppointments && canCreateAppointmentConnections;
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const treatmentViewModels = useMedicalTreatmentViewModel(matter);
  const selectedTreatment = treatmentViewModels.find(
    (treatment) => treatment.id === treatmentId,
  );
  const selectedAppointments = useMemo(() => {
    if (!selectedTreatment) return [];

    return selectedTreatment.appointments.filter((appointment) =>
      appointmentIds.includes(appointment.id),
    );
  }, [appointmentIds, selectedTreatment]);

  const sharedRecordIds = useMemo(() => {
    const recordCollections = selectedAppointments.map((appointment) =>
      getAppointmentRecordIds(appointment),
    );
    return getCommonIds(recordCollections);
  }, [selectedAppointments]);

  const sharedBillIds = useMemo(() => {
    const billCollections = selectedAppointments.map((appointment) =>
      getAppointmentBillIds(appointment),
    );
    return getCommonIds(billCollections);
  }, [selectedAppointments]);

  const allAppointmentsHaveRecordsNotApplicable = useMemo(() => {
    if (selectedAppointments.length === 0) return false;
    return selectedAppointments.every(
      (appointment) => appointment.recordNotApplicable === true,
    );
  }, [selectedAppointments]);

  const allAppointmentsHaveBillsNotApplicable = useMemo(() => {
    if (selectedAppointments.length === 0) return false;
    return selectedAppointments.every(
      (appointment) => appointment.billNotApplicable === true,
    );
  }, [selectedAppointments]);

  const formValues = useMemo(
    () => ({
      medicalTreatmentRecordId: allAppointmentsHaveRecordsNotApplicable
        ? []
        : sharedRecordIds,
      medicalTreatmentBillId: allAppointmentsHaveBillsNotApplicable
        ? []
        : sharedBillIds,
      recordNotApplicable: allAppointmentsHaveRecordsNotApplicable,
      billNotApplicable: allAppointmentsHaveBillsNotApplicable,
    }),
    [
      sharedBillIds,
      sharedRecordIds,
      allAppointmentsHaveRecordsNotApplicable,
      allAppointmentsHaveBillsNotApplicable,
    ],
  );

  const associationForm = useForm<z.infer<typeof associateAppointmentsSchema>>({
    resolver: zodResolver(associateAppointmentsSchema),
    values: formValues,
  });

  const updateAppointment = useUpdateMedicalTreatmentAppointment({
    suppressSuccessToast: true,
  });

  const updateAppointmentConnections =
    useUpdateMedicalTreatmentAppointmentConnections({});

  const recordsNotApplicable = Boolean(selectedTreatment?.recordNotApplicable);
  const billsNotApplicable = Boolean(selectedTreatment?.billNotApplicable);
  const nothingToAssociate = recordsNotApplicable && billsNotApplicable;

  const handleSubmit = associationForm.handleSubmit(async (values) => {
    if (!canManageAssociations) return;
    if (
      !selectedTreatment ||
      nothingToAssociate ||
      selectedAppointments.length === 0
    ) {
      return;
    }
    const selectedRecordIds = values.medicalTreatmentRecordId ?? [];
    const selectedBillIds = values.medicalTreatmentBillId ?? [];
    const recordsNotApplicableSelected = values.recordNotApplicable ?? false;
    const billsNotApplicableSelected = values.billNotApplicable ?? false;

    try {
      for (const appointment of selectedAppointments) {
        const appointmentId = appointment.id;
        if (appointmentId.length === 0) {
          continue;
        }

        // Get the raw appointment object to access dateModified
        const rawAppointment = matter.medicalTreatmentAppointments.find(
          (apt) => apt.id === appointmentId,
        );
        if (!rawAppointment) {
          continue;
        }

        // Update the appointment's "not applicable" flags
        await updateAppointment.mutateAsync({
          matterId: matter.id,
          id: appointmentId,
          dateModified: rawAppointment.dateModified,
          recordNotApplicable: recordsNotApplicableSelected,
          billNotApplicable: billsNotApplicableSelected,
          // Include other required fields from the appointment
          date: rawAppointment.date,
          type: rawAppointment.type,
          medicalTreatmentId: rawAppointment.medicalTreatmentId,
        });

        const currentRecordIds = getAppointmentRecordIds(appointment);
        const currentBillIds = getAppointmentBillIds(appointment);

        // Only update connections if "not applicable" is not selected
        if (!recordsNotApplicable && !recordsNotApplicableSelected) {
          const mergedRecordIds = Array.from(
            new Set([...currentRecordIds, ...selectedRecordIds]),
          );
          await updateAppointmentConnections.mutateAsync({
            matter,
            existingItems: currentRecordIds,
            newItems: mergedRecordIds,
            appointmentId,
            itemType: "record",
          });
        }
        if (!billsNotApplicable && !billsNotApplicableSelected) {
          const mergedBillIds = Array.from(
            new Set([...currentBillIds, ...selectedBillIds]),
          );
          await updateAppointmentConnections.mutateAsync({
            matter,
            existingItems: currentBillIds,
            newItems: mergedBillIds,
            appointmentId,
            itemType: "bill",
          });
        }
      }
      toast.success("Associations updated for selected appointments");
      onSuccess?.();
      setOpen(false);
    } catch {
      // Errors are handled inside the mutation hooks with toasts.
    }
  });

  const isFormLoading =
    updateAppointmentConnections.isPending || updateAppointment.isPending;

  return (
    <Slideover
      open={open}
      setOpen={setOpen}
      title={title}
      description={description}
      titleIcon={<MedicalTreatmentAppointmentIcon className="h-4 w-4" />}
      isDirty={associationForm.formState.isDirty}
    >
      <Form {...associationForm}>
        <form
          className="relative flex grow flex-col overflow-hidden"
          onSubmit={handleSubmit}
        >
          {isFormLoading && <FormLoadingOverlay />}
          <fieldset
            className={slideoverFieldsetClassName}
            disabled={
              isFormLoading ||
              nothingToAssociate ||
              !selectedTreatment ||
              !canManageAssociations
            }
          >
            <div className="col-span-full space-y-3">
              <Typography weight="semibold">
                {selectedAppointments.length} selected treatment date
                {selectedAppointments.length === 1 ? "" : "s"}
              </Typography>
              <div className="bg-muted/40 rounded-md border p-3">
                {selectedAppointments.length === 0 ? (
                  <Typography size="sm" className="text-muted-foreground">
                    No treatment dates selected.
                  </Typography>
                ) : (
                  <ul className="space-y-2">
                    {selectedAppointments.map((appointment) => {
                      const dateLabel = appointment.date
                        ? formatDate(appointment.date)
                        : "No date";
                      const timeLabel = appointment.time
                        ? formatTime(appointment.time)
                        : "";
                      return (
                        <li key={appointment.id} className="text-sm">
                          <span className="font-medium">{dateLabel}</span>
                          {timeLabel && (
                            <span className="text-muted-foreground">
                              {` • ${timeLabel}`}
                            </span>
                          )}
                          {appointment.description && (
                            <span className="text-muted-foreground block">
                              {appointment.description}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            {!recordsNotApplicable && selectedTreatment && (
              <SelectTreatmentRecordFormField
                register={associationForm.register("medicalTreatmentRecordId")}
                registerNotApplicable={associationForm.register(
                  "recordNotApplicable",
                )}
                label="Associated medical records"
                matter={matter}
                treatment={selectedTreatment}
              />
            )}
            {!billsNotApplicable && selectedTreatment && (
              <SelectTreatmentBillsFormField
                register={associationForm.register("medicalTreatmentBillId")}
                registerNotApplicable={associationForm.register(
                  "billNotApplicable",
                )}
                label="Associated billing records"
                matter={matter}
                treatment={selectedTreatment}
              />
            )}
            {nothingToAssociate && (
              <div className="col-span-full rounded-md border border-dashed p-3">
                <Typography size="sm" className="text-muted-foreground">
                  Medical records and billing records are marked as not
                  applicable for this treatment.
                </Typography>
              </div>
            )}
          </fieldset>
          <div className="flex grow" />
          <SheetFooter addSlideoverStyles>
            <FormCancelButton
              setOpen={setOpen}
              isDirty={associationForm.formState.isDirty}
            />
            <FormSubmitButton
              variant="default"
              disabled={!canManageAssociations}
              loading={isFormLoading}
              overrideDisabled={
                selectedAppointments.length === 0 || nothingToAssociate
              }
            />
          </SheetFooter>
        </form>
      </Form>
    </Slideover>
  );
};
